'use client'
import React, { useState, useRef, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFilter,
  faMagnifyingGlass,
  faRefresh,
  faAnglesLeft,
  faAnglesRight,
  faChevronLeft,
  faChevronRight,
  faFileExcel,
} from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  outlinedInputClasses,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format, isValid } from 'date-fns'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import Button from 'src/components/era-package/forms/Button'
import ChildCard from 'src/components/era-package/shared/ChildCard'
import { Filter } from 'src/components/era-package/filters'
import { useServerPagination } from '../hooks/useServerPagination'
import AbiBillingAdminApiUrls from '../../../apiUrls/AbiBillingAdminApiUrls'
import TprApiUrls from '../../../apiUrls/TprApiUrls'

const columnHelper = createColumnHelper()

const buildBillingAdminColumns = (partyOptions) => [
  columnHelper.accessor('partyId', {
    header: () => 'Party Name',
    cell: (info) => {
      const id = info.getValue()
      const match = partyOptions.find((o) => String(o.value) === String(id))
      return match ? match.label : (id ?? '-')
    },
    filterFn: 'includesString',
    meta: {
      filterVariant: 'selectOptions',
      filterOptions: partyOptions,
    },
  }),
  columnHelper.accessor('serviceName', {
    header: () => 'Service',
    cell: (info) => info.getValue() ?? '-',
    filterFn: 'includesString',
  }),
  columnHelper.accessor('nbsUserId', {
    header: () => 'User ID',
    cell: (info) => info.getValue() ?? '-',
    filterFn: 'includesString',
  }),
  columnHelper.accessor('transactionDate', {
    header: () => 'Transaction Date',
    cell: (info) => info.getValue() ?? '-',
    filterFn: 'includesString',
    meta: {
      filterVariant: 'dateRange',
    },
  }),
  columnHelper.accessor('filerCode', {
    header: () => 'Filer Code',
    cell: (info) => info.getValue() ?? '-',
    filterFn: 'includesString',
  }),
  columnHelper.accessor('refNo1', {
    header: () => 'Ref No. 1',
    cell: (info) => info.getValue() ?? '-',
    filterFn: 'includesString',
  }),
  columnHelper.accessor('refNo2', {
    header: () => 'Ref No. 2',
    cell: (info) => info.getValue() ?? '-',
    filterFn: 'includesString',
  }),
  columnHelper.accessor('refNo3', {
    header: () => 'Ref No. 3',
    cell: (info) => info.getValue() ?? '-',
    filterFn: 'includesString',
  }),
  columnHelper.accessor('refNo4', {
    header: () => 'Ref No. 4',
    cell: (info) => info.getValue() ?? '-',
    filterFn: 'includesString',
  }),
  columnHelper.accessor('messageType', {
    header: () => 'Message Type',
    cell: (info) => info.getValue() ?? '-',
    filterFn: 'includesString',
  }),
  columnHelper.accessor('transactionId', {
    header: () => 'Transaction ID',
    cell: (info) => info.getValue() ?? '-',
    filterFn: 'includesString',
  }),
  columnHelper.accessor('status', {
    header: () => 'Status',
    cell: (info) => info.getValue() ?? '-',
    filterFn: 'includesString',
  }),
]

const COLUMN_TO_PARAM = {
  partyId: 'partyId',
  serviceName: 'serviceName',
  nbsUserId: 'nbsUserId',
  transactionDate: 'transactionDate',
  filerCode: 'filerCode',
  refNo1: 'refNo1',
  refNo2: 'refNo2',
  refNo3: 'refNo3',
  refNo4: 'refNo4',
  messageType: 'messageType',
  transactionId: 'transactionId',
  status: 'status',
}

const DEFAULT_PARAMS = {
  page: 0,
  size: 10,
  search: '',
  partyId: '',
  serviceName: '',
  nbsUserId: '',
  transactionDate: '',
  transactionDateStart: '',
  transactionDateEnd: '',
  filerCode: '',
  refNo1: '',
  refNo2: '',
  refNo3: '',
  refNo4: '',
  messageType: '',
  transactionId: '',
  status: '',
}

// ─── Excel export helper (no external library needed) ───────────────────────
const exportToExcel = (rows, partyOptions) => {
  const headers = [
    'Party Name',
    'Service',
    'User ID',
    'Transaction Date',
    'Filer Code',
    'Reference',
    'Ref No. 3',
    'Ref No. 4',
    'Message Type',
    'Transaction ID',
    'Status',
  ]

  const resolveParty = (id) => {
    const match = partyOptions.find((o) => String(o.value) === String(id))
    return match ? match.label : (id ?? '')
  }

  const dataRows = rows.map((r) => [
    resolveParty(r.partyId),
    r.serviceName ?? '',
    r.nbsUserId ?? '',
    r.transactionDate ?? '',
    r.filerCode ?? '',
    r.reference ?? '',
    r.refNo3 ?? '',
    r.refNo4 ?? '',
    r.messageType ?? '',
    r.transactionId ?? '',
    r.status ?? '',
  ])

  // Build a simple XML-based .xls (Excel 2003 XML Spreadsheet) — no extra deps
  const xmlRows = [headers, ...dataRows]
    .map(
      (row) =>
        '<Row>' +
        row
          .map(
            (cell) =>
              `<Cell><Data ss:Type="String">${String(cell).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</Data></Cell>`
          )
          .join('') +
        '</Row>'
    )
    .join('')

  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  <Worksheet ss:Name="Billing Report">
    <Table>${xmlRows}</Table>
  </Worksheet>
</Workbook>`

  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', 'abi-billing-report-admin.xls')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
// ────────────────────────────────────────────────────────────────────────────

const BillingRecordAdminList = () => {
  const theme = useTheme()

  const [partyOptions, setPartyOptions] = useState([])
  const [searchInput, setSearchInput] = useState('')
  const [columnFilters, setColumnFilters] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [sorting, setSorting] = useState([{ id: 'transactionDate', desc: true }])
  const debounceRef = useRef(null)

  useEffect(() => {
    const fetchPartyOptions = async () => {
      try {
        const response = await fetch(TprApiUrls.GET, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
          },
          body: JSON.stringify({
            baseFilterDTO: {
              filterType: 'AND',
              filterProperties: [],
              sortProperties: [],
            },
          }),
        })
        const json = await response.json()
        const content = json?.content || json?.data?.content || []
        const options = content.map((party) => ({
          value: String(party.id),
          label: party.name || String(party.id),
        }))
        setPartyOptions(options)
      } catch (error) {
        console.error('Error fetching party options:', error)
      }
    }
    fetchPartyOptions()
  }, [])

  const billingAdminColumns = React.useMemo(
    () => buildBillingAdminColumns(partyOptions),
    [partyOptions]
  )

  const { data, totalElements, totalPages, loading, params, fetchPage, updateParams } =
    useServerPagination(AbiBillingAdminApiUrls.GET, DEFAULT_PARAMS)

  const pageIndex = params.page
  const pageSize = params.size

  const table = useReactTable({
    data,
    columns: billingAdminColumns,
    filterFns: {},
    state: {
      columnFilters,
      sorting,
      pagination: { pageIndex, pageSize },
    },
    manualPagination: true,
    pageCount: totalPages,
    onSortingChange: setSorting,
    onColumnFiltersChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnFilters) : updater
      setColumnFilters(next)
      const mapped = {}
      Object.values(COLUMN_TO_PARAM).forEach((f) => (mapped[f] = ''))
      let transactionDateStart = ''
      let transactionDateEnd = ''
      next.forEach(({ id, value }) => {
        if (id === 'transactionDate' && Array.isArray(value)) {
          const [start, end] = value
          transactionDateStart = start && isValid(start) ? format(start, 'yyyy-MM-dd') : ''
          transactionDateEnd = end && isValid(end) ? format(end, 'yyyy-MM-dd') : ''
        } else {
        const key = COLUMN_TO_PARAM[id]
        if (key) mapped[key] = value ?? ''
        }
      })
      updateParams({ ...mapped, transactionDateStart, transactionDateEnd, page: 0 })
    },

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const handleDownload = () => exportToExcel(data, partyOptions)

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Stack spacing={2}>
        {/* ── Search / Filter Controls ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
        >
          {/* Global search */}
        <TextField
          size="small"
          placeholder="Search all columns..."
          value={searchInput}
          onChange={(e) => {
            const value = e.target.value
            setSearchInput(value)
            clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
              updateParams({ search: value, page: 0 })
            }, 500)
            }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FontAwesomeIcon icon={faMagnifyingGlass} style={{ width: 16, height: 16 }} />
              </InputAdornment>
            ),
            }}
          sx={{
            maxWidth: { xs: '100%', sm: '300px' },
            [`& .${outlinedInputClasses.notchedOutline}`]: { inset: 0 },
          }}
        />

          {/* Column filters toggle */}
        <Button
          variant="outlined"
          onClick={() => setShowFilters((prev) => !prev)}
          startIcon={<FontAwesomeIcon icon={faFilter} style={{ width: 16, height: 16 }} />}
        >
          {showFilters ? 'Hide Filters' : 'Show Column Filters'}
        </Button>

          {/* Download Excel */}
          <Button
            variant="outlined"
            onClick={handleDownload}
            disabled={data.length === 0}
            startIcon={<FontAwesomeIcon icon={faFileExcel} style={{ width: 16, height: 16 }} />}
          >
            Download Excel
          </Button>
    </Stack>

      <ChildCard sx={{ p: 0, mb: -3 }}>
        <Stack>
          <TableContainer>
            <Table>
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <React.Fragment key={headerGroup.id}>
                    <TableRow>
                      {headerGroup.headers.map((header) => (
                        <TableCell key={header.id}>
                          <Typography
                            variant="body"
                            color="primary.main"
                            fontWeight="600"
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : '',
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                            {{ asc: ' \uD83D\uDD3C', desc: ' \uD83D\uDD3D' }[header.column.getIsSorted()] ?? null}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                    {showFilters && (
                      <TableRow>
                        {headerGroup.headers.map((header) => (
                          <TableCell key={`filter-${header.id}`}>
                            {header.column.getCanFilter() ? (
                              <Filter column={header.column} />
                            ) : null}
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} hover>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} sx={{ whiteSpace: 'nowrap' }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={billingAdminColumns.length} align="center">
                      <Typography variant="body2" color="text.secondary">Loading...</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={billingAdminColumns.length} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No billing records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />

          <Stack
            gap={1}
            p={3}
            alignItems="center"
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => fetchPage()}
                startIcon={
                  <FontAwesomeIcon
                    icon={faRefresh}
                    style={{ width: 16, height: 16 }}
                    spin={loading}
                  />
                }
              >
                Refresh data
              </Button>
              <Typography variant="body1">
                {totalElements} Rows
              </Typography>
            </Box>

            <Box
              sx={{ display: { xs: 'block', sm: 'flex' } }}
              alignItems="center"
              gap={1}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body1">Page</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {pageIndex + 1} of {totalPages}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                | Go to page:
                <TextField
                  type="number"
                  inputProps={{ min: 1, max: totalPages }}
                  defaultValue={pageIndex + 1}
                  onChange={(e) => {
                    const val = Number(e.target.value)
                    if (!val || val < 1) return
                    updateParams({ page: Math.min(val, totalPages) - 1 })
                  }}
                  size="small"
                />
              </Stack>
              <Select
                value={pageSize}
                onChange={(e) => updateParams({ size: Number(e.target.value), page: 0 })}
                size="small"
              >
                {[5, 10, 25, 50, 100].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>

              <IconButton
                size="small"
                onClick={() => updateParams({ page: 0 })}
                disabled={pageIndex === 0}
              >
                <FontAwesomeIcon icon={faAnglesLeft} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => updateParams({ page: pageIndex - 1 })}
                disabled={pageIndex === 0}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => updateParams({ page: pageIndex + 1 })}
                disabled={pageIndex >= totalPages - 1}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => updateParams({ page: totalPages - 1 })}
                disabled={pageIndex >= totalPages - 1}
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </IconButton>
            </Box>
          </Stack>
        </Stack>
      </ChildCard>
    </Stack>
    </LocalizationProvider>
  )
}

export default BillingRecordAdminList